import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Grid,
  TablePagination,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  DirectionsBus,
  Visibility,
  VisibilityOff,
  Person,
  ContactEmergency,
  Badge,
  Work,
} from '@mui/icons-material';
import api from '../../services/api';


/**
 * Componente de Gestión de Choferes Completo
 * Path: backoffice-web/src/pages/Choferes/Choferes.js
 * Última modificación: 2025-12-30 23:30
 * Dev: cpalacios01@gmail.com | +595994648273
 */

// TabPanel Component
function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Choferes() {
  const [choferes, setChoferes] = useState([]);
  const [filteredChoferes, setFilteredChoferes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAssignBus, setOpenAssignBus] = useState(false);
  const [editingChofer, setEditingChofer] = useState(null);
  const [assigningChofer, setAssigningChofer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [busFilter, setBusFilter] = useState('todos');
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    // Datos básicos
    nombre: '',
    email: '',
    password: '',
    
    // Datos personales
    cedula: '',
    fecha_nacimiento: '',
    telefono: '',
    direccion: '',
    grupo_sanguineo: '',
    
    // Datos de licencia
    numero_licencia: '',
    tipo_licencia: '',
    licencia_vencimiento: '',
    
    // Contacto de emergencia
    emergencia_nombre: '',
    emergencia_telefono: '',
    emergencia_relacion: '',
    
    // Datos laborales
    fecha_ingreso: '',
  });

  const [selectedBusId, setSelectedBusId] = useState('');

  useEffect(() => {
    fetchChoferes();
    fetchBuses();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [choferes, searchTerm, busFilter]);

  const fetchChoferes = async () => {
    try {
      const response = await api.get('/choferes');
      setChoferes(response.data);
    } catch (error) {
      console.error('Error al cargar choferes:', error);
      setError('Error al cargar los choferes');
    }
  };

  const fetchBuses = async () => {
    try {
      const response = await api.get('/buses-disponibles');
      setBuses(response.data);
    } catch (error) {
      console.error('Error al cargar buses:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...choferes];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(chofer => 
        (chofer.nombre?.toLowerCase() || '').includes(search) ||
        (chofer.email?.toLowerCase() || '').includes(search) ||
        (chofer.cedula?.toLowerCase() || '').includes(search) ||
        (chofer.telefono?.toLowerCase() || '').includes(search) ||
        (chofer.bus?.numero?.toLowerCase() || '').includes(search)
      );
    }

    if (busFilter === 'con-bus') {
      filtered = filtered.filter(chofer => chofer.bus_id !== null);
    } else if (busFilter === 'sin-bus') {
      filtered = filtered.filter(chofer => chofer.bus_id === null);
    }

    setFilteredChoferes(filtered);
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleBusFilterChange = (e) => {
    setBusFilter(e.target.value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setBusFilter('todos');
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      cedula: '',
      fecha_nacimiento: '',
      telefono: '',
      direccion: '',
      grupo_sanguineo: '',
      numero_licencia: '',
      tipo_licencia: '',
      licencia_vencimiento: '',
      emergencia_nombre: '',
      emergencia_telefono: '',
      emergencia_relacion: '',
      fecha_ingreso: '',
    });
  };

  const handleOpenDialog = (chofer = null) => {
    if (chofer) {
      setEditingChofer(chofer);
      setFormData({
        nombre: chofer.nombre || '',
        email: chofer.email || '',
        password: '',
        cedula: chofer.cedula || '',
        fecha_nacimiento: chofer.fecha_nacimiento || '',
        telefono: chofer.telefono || '',
        direccion: chofer.direccion || '',
        grupo_sanguineo: chofer.grupo_sanguineo || '',
        numero_licencia: chofer.numero_licencia || '',
        tipo_licencia: chofer.tipo_licencia || '',
        licencia_vencimiento: chofer.licencia_vencimiento || '',
        emergencia_nombre: chofer.emergencia_nombre || '',
        emergencia_telefono: chofer.emergencia_telefono || '',
        emergencia_relacion: chofer.emergencia_relacion || '',
        fecha_ingreso: chofer.fecha_ingreso || '',
      });
    } else {
      setEditingChofer(null);
      resetForm();
    }
    setOpenDialog(true);
    setCurrentTab(0);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingChofer(null);
    resetForm();
    setError('');
    setShowPassword(false);
    setCurrentTab(0);
  };

  const handleOpenAssignBus = (chofer) => {
    setAssigningChofer(chofer);
    setSelectedBusId(chofer.bus_id || '');
    setOpenAssignBus(true);
    setError('');
    setSuccess('');
  };

  const handleCloseAssignBus = () => {
    setOpenAssignBus(false);
    setAssigningChofer(null);
    setSelectedBusId('');
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const dataToSend = { ...formData };
      
      // Si no hay password al editar, no enviarlo
      if (editingChofer && !dataToSend.password) {
        delete dataToSend.password;
      }
      
      // Validar password al crear
      if (!editingChofer && !dataToSend.password) {
        setError('La contraseña es requerida');
        setLoading(false);
        return;
      }

      if (editingChofer) {
        await api.put(`/choferes/${editingChofer.id}`, dataToSend);
        setSuccess('Chofer actualizado exitosamente');
      } else {
        await api.post('/choferes', dataToSend);
        setSuccess('Chofer creado exitosamente');
      }

      await fetchChoferes();
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);

    } catch (error) {
      console.error('Error al guardar chofer:', error);
      
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        setError(`Error de validación: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Error al guardar el chofer');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignBus = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post(`/choferes/${assigningChofer.id}/asignar-bus`, {
        bus_id: selectedBusId || null,
      });
      
      setSuccess(selectedBusId ? 'Bus asignado exitosamente' : 'Bus desasignado exitosamente');
      await fetchChoferes();
      await fetchBuses();
      
      setTimeout(() => {
        handleCloseAssignBus();
      }, 1500);

    } catch (error) {
      console.error('Error al asignar bus:', error);
      setError(error.response?.data?.message || 'Error al asignar el bus');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este chofer?')) {
      return;
    }

    try {
      await api.delete(`/choferes/${id}`);
      setSuccess('Chofer eliminado exitosamente');
      fetchChoferes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al eliminar chofer:', error);
      setError(error.response?.data?.message || 'Error al eliminar el chofer');
      setTimeout(() => setError(''), 3000);
    }
  };

  const paginatedChoferes = filteredChoferes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Gestión de Choferes</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Chofer
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, cédula, teléfono, email o bus..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Asignación de Bus</InputLabel>
              <Select
                value={busFilter}
                label="Asignación de Bus"
                onChange={handleBusFilterChange}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                }
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="con-bus">Con Bus Asignado</MenuItem>
                <MenuItem value="sin-bus">Sin Bus Asignado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleClearFilters}
            >
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredChoferes.length} de {choferes.length} choferes
          </Typography>
        </Box>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Cédula</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Bus Asignado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedChoferes.map((chofer) => (
              <TableRow key={chofer.id}>
                <TableCell>{chofer.id}</TableCell>
                <TableCell>{chofer.nombre}</TableCell>
                <TableCell>{chofer.cedula || '-'}</TableCell>
                <TableCell>{chofer.telefono || '-'}</TableCell>
                <TableCell>{chofer.email}</TableCell>
                <TableCell>
                  {chofer.bus ? (
                    <Chip
                      label={`Bus ${chofer.bus.numero}`}
                      color="primary"
                      size="small"
                    />
                  ) : (
                    <Chip label="Sin asignar" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(chofer)}
                    title="Editar"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="secondary"
                    onClick={() => handleOpenAssignBus(chofer)}
                    title="Asignar Bus"
                  >
                    <DirectionsBus />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(chofer.id)}
                    title="Eliminar"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedChoferes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {searchTerm || busFilter !== 'todos'
                    ? 'No se encontraron choferes con los filtros aplicados'
                    : 'No hay choferes registrados'
                  }
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredChoferes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </TableContainer>

      {/* Diálogo crear/editar chofer con pestañas */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingChofer ? 'Editar Chofer' : 'Nuevo Chofer'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab icon={<Person />} label="Datos Básicos" />
              <Tab icon={<Badge />} label="Licencia" />
              <Tab icon={<ContactEmergency />} label="Emergencia" />
              <Tab icon={<Work />} label="Laboral" />
            </Tabs>
          </Box>

          <Box component="form">
            {/* Tab 1: Datos Básicos */}
            <TabPanel value={currentTab} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre Completo *"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Cédula de Identidad"
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email *"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={editingChofer ? "Contraseña (dejar vacío para no cambiar)" : "Contraseña *"}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingChofer}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Nacimiento"
                    name="fecha_nacimiento"
                    type="date"
                    value={formData.fecha_nacimiento}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Grupo Sanguíneo"
                    name="grupo_sanguineo"
                    value={formData.grupo_sanguineo}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Seleccionar...</MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 2: Licencia */}
            <TabPanel value={currentTab} index={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Número de Licencia"
                    name="numero_licencia"
                    value={formData.numero_licencia}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Tipo de Licencia"
                    name="tipo_licencia"
                    value={formData.tipo_licencia}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Seleccionar...</MenuItem>
                    <MenuItem value="A">A - Motocicletas</MenuItem>
                    <MenuItem value="B">B - Autos particulares</MenuItem>
                    <MenuItem value="C">C - Camiones</MenuItem>
                    <MenuItem value="D">D - Transporte público</MenuItem>
                    <MenuItem value="E">E - Vehículos pesados</MenuItem>
                    <MenuItem value="F">F - Vehículos especiales</MenuItem>
                    <MenuItem value="G">G - Maquinaria agrícola</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Vencimiento"
                    name="licencia_vencimiento"
                    type="date"
                    value={formData.licencia_vencimiento}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 3: Contacto de Emergencia */}
            <TabPanel value={currentTab} index={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre del Contacto"
                    name="emergencia_nombre"
                    value={formData.emergencia_nombre}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Teléfono del Contacto"
                    name="emergencia_telefono"
                    value={formData.emergencia_telefono}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Relación"
                    name="emergencia_relacion"
                    value={formData.emergencia_relacion}
                    onChange={handleChange}
                  >
                    <MenuItem value="">Seleccionar...</MenuItem>
                    <MenuItem value="Padre">Padre</MenuItem>
                    <MenuItem value="Madre">Madre</MenuItem>
                    <MenuItem value="Esposo/a">Esposo/a</MenuItem>
                    <MenuItem value="Hijo/a">Hijo/a</MenuItem>
                    <MenuItem value="Hermano/a">Hermano/a</MenuItem>
                    <MenuItem value="Otro">Otro</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 4: Datos Laborales */}
            <TabPanel value={currentTab} index={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Ingreso"
                    name="fecha_ingreso"
                    type="date"
                    value={formData.fecha_ingreso}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </TabPanel>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.nombre || !formData.email}
          >
            {loading ? 'Guardando...' : editingChofer ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo asignar bus */}
      <Dialog open={openAssignBus} onClose={handleCloseAssignBus} maxWidth="sm" fullWidth>
        <DialogTitle>
          Asignar Bus a {assigningChofer?.nombre}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Bus</InputLabel>
              <Select
                value={selectedBusId}
                label="Bus"
                onChange={(e) => setSelectedBusId(e.target.value)}
              >
                <MenuItem value="">Sin asignar</MenuItem>
                {buses.map((bus) => (
                  <MenuItem key={bus.id} value={bus.id}>
                    Bus {bus.numero} - {bus.patente}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignBus}>Cancelar</Button>
          <Button
            onClick={handleAssignBus}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Asignar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273
